import { useRef, useState } from "react";
import { Button } from "../../components/buttons/button";
import { MiniHeader } from "../../components/typography/miniHeader";
import { PageHeader } from "../../components/typography/pageHeader";
import { BgWrapper } from "../../components/wrapper/bgWrapper";
import { useAppSelector } from "../../redux/hooks";
import { EventArchivesSettings } from "./eventArchivesSettings";
import { PayoutSettings } from "./payoutSettings";
import { ProfileSettings } from "./profileSettings";
import { SecuritySettings } from "./securitySettings";

type Section = "profile" | "payouts" | "security" | "archives";

const sectionActions: {
  title: string;
  to: Section;
  icon: string;
}[] = [
  { title: "Profile", to: "profile", icon: "avatar" },
  { title: "Payout", to: "payouts", icon: "bank" },
  { title: "Security", to: "security", icon: "shield" },
  { title: "Archives", to: "archives", icon: "archive" },
];

export const Settings = () => {
  const [activeSection, setActiveSection] = useState<Section>("profile");

  const { profile } = useAppSelector((state) => state.auth);

  const pageRefs = useRef<HTMLDivElement[] | null>([]);

  return (
    <div className="w-full">
      <PageHeader header={"Account Settings"} />

      <div className="relative mt-8 flex items-start gap-0">
        <div className="sticky top-[34px] hidden h-fit w-full max-w-[200px] flex-col md:flex">
          {sectionActions.map((section, idx) => {
            if (
              profile?.signupMethod !== "Email and password" &&
              section.to === "security"
            ) {
              return undefined;
            }

            return (
              <Button
                key={section.to}
                onClick={() => {
                  setActiveSection(section.to);
                  if (pageRefs.current && pageRefs.current.length > idx) {
                    pageRefs.current[idx].scrollIntoView({
                      behavior: "smooth",
                    });
                  }
                }}
                icon={section.icon}
                iconDimension={20}
                iconClassName="text-grey-400"
                type="button"
                variation={activeSection === section.to ? "white" : "bland"}
                text={section.title}
                className="!justify-start !rounded-2xl !p-3 !text-left !text-sm"
              />
            );
          })}
        </div>

        <div className="flex flex-1 justify-center md:px-4">
          <div className="flex w-full max-w-[628px] flex-col gap-2.5 md:pb-80">
            <div
              className="flex w-full flex-col gap-[2px]"
              ref={(el) => {
                if (pageRefs.current && el) {
                  pageRefs.current[0] = el;
                }
              }}
            >
              <BgWrapper>
                <MiniHeader header="Profile" />
              </BgWrapper>
              <ProfileSettings
                onEditSuccess={() => {
                  if (pageRefs.current && pageRefs.current.length > 0) {
                    pageRefs.current[0].scrollIntoView({
                      behavior: "smooth",
                    });
                  }
                }}
              />
            </div>

            <div
              className="flex w-full flex-col gap-[2px]"
              ref={(el) => {
                if (pageRefs.current && el) {
                  pageRefs.current[1] = el;
                }
              }}
            >
              <BgWrapper>
                <MiniHeader
                  header="Payout Details"
                  subheader="You can only add one payout account per currency."
                />
              </BgWrapper>
              <PayoutSettings />
            </div>

            {profile?.signupMethod === "Email and password" ? (
              <div
                className="flex w-full flex-col gap-[2px]"
                ref={(el) => {
                  if (pageRefs.current && el) {
                    pageRefs.current[2] = el;
                  }
                }}
              >
                <BgWrapper className="flex items-center justify-between gap-3">
                  <MiniHeader header="Security" />
                </BgWrapper>
                <SecuritySettings />
              </div>
            ) : undefined}

            <div
              className="flex w-full flex-col gap-[2px]"
              ref={(el) => {
                if (pageRefs.current && el) {
                  pageRefs.current[3] = el;
                }
              }}
            >
              <BgWrapper>
                <MiniHeader header="Event Archives" />
              </BgWrapper>
              <EventArchivesSettings />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
